import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export type ThemeAccent = 'indigo' | 'emerald' | 'violet' | 'sky' | 'rose';
export type ThemeMode = 'light' | 'dark';

export interface ClientPwaSettings {
  accent: ThemeAccent;
  emailNotifications: boolean;
  pushNotifications: boolean;
  largeText: boolean;
}

const defaults: ClientPwaSettings = {
  accent: 'indigo',
  emailNotifications: true,
  pushNotifications: false,
  largeText: false,
};

const STORAGE_KEY = 'client-pwa-settings';

interface SettingsContextValue {
  settings: ClientPwaSettings;
  update: <K extends keyof ClientPwaSettings>(key: K, value: ClientPwaSettings[K]) => void;
  t: (key: string) => string;
  themeMode: ThemeMode;
  setThemeMode: (m: ThemeMode) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

// English-only label dictionary. Kept as a function so existing pages that call
// t('home') / t('files') / etc. still work without changes.
const labels: Record<string, string> = {
  welcome_back: 'Welcome back',
  home: 'Home',
  files: 'Files',
  insights: 'Insights',
  help: 'Ask Us',
  settings: 'Settings',
  business_health: 'Business health',
  ytd_revenue: 'YTD Revenue',
  net_profit: 'Net Profit',
  next_deadline: 'Next deadline',
  latest_from_accountant: 'Latest from your accountant',
  recent_files: 'Recent files',
  ask_a_question: 'Ask a question',
  ai_replies_in_seconds: 'AI replies in seconds',
  my_files: 'My Files',
  all_documents: 'All documents shared by your accountant',
  search_files: 'Search files...',
  requested_files: 'Files your accountant requested',
  upload: 'Upload',
  appearance: 'Appearance',
  account: 'Account',
  notifications: 'Notifications',
  change_password: 'Change password',
  email_notifications: 'Email notifications',
  push_notifications: 'Push notifications',
  large_text: 'Larger text',
  theme_color: 'Accent colour',
  theme_mode: 'Theme',
  light: 'Light',
  dark: 'Dark',
  about: 'About',
  powered_by: 'Powered by',
  chat: 'Chat',
  timeline: 'Timeline',
  ask_anything: 'Ask anything — AI tries first, then your accountant',
  type_message: 'Ask a question...',
  worth_discussing: 'Worth discussing with your accountant',
  how_you_compare: 'How you compare',
  where_money_goes: 'Where your money goes',
  revenue_and_profit: 'Revenue & profit',
  saved: 'Saved',
  save_changes: 'Save changes',
  current_password: 'Current password',
  new_password: 'New password',
  confirm_password: 'Confirm new password',
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState<ClientPwaSettings>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch {}
    return defaults;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch {}
    // English-only, LTR
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'en';
  }, [settings]);

  const update = <K extends keyof ClientPwaSettings>(key: K, value: ClientPwaSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const t = (key: string) => labels[key] ?? key;

  return (
    <SettingsContext.Provider value={{
      settings, update, t,
      themeMode: theme,
      setThemeMode: (m: ThemeMode) => setTheme(m),
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useClientSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useClientSettings must be used inside SettingsProvider');
  return ctx;
}

export const accentClasses: Record<ThemeAccent, { gradient: string; bg: string; text: string; ring: string }> = {
  // Default = RoniDoc primary indigo (HSL 243 75% 59%) — matches the rest of the app
  indigo: {
    gradient: 'from-indigo-500 to-violet-600',
    bg: 'bg-indigo-500',
    text: 'text-indigo-600 dark:text-indigo-400',
    ring: 'ring-indigo-500',
  },
  emerald: {
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-emerald-500',
  },
  violet: {
    gradient: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-500',
    text: 'text-violet-600 dark:text-violet-400',
    ring: 'ring-violet-500',
  },
  sky: {
    gradient: 'from-sky-500 to-blue-600',
    bg: 'bg-sky-500',
    text: 'text-sky-600 dark:text-sky-400',
    ring: 'ring-sky-500',
  },
  rose: {
    gradient: 'from-rose-500 to-pink-600',
    bg: 'bg-rose-500',
    text: 'text-rose-600 dark:text-rose-400',
    ring: 'ring-rose-500',
  },
};
